/**
 * CR310: V2 Override - Simplified (V1 Removed)
 * 
 * With V1 completely removed, this module now just checks:
 * 1. ?dealCardV2=0 → force V1 (for emergency rollback only)
 * 2. dealCardSpecV2_enabled = true → V2 always
 * 3. else → V2 (default, since V1 is removed)
 * 
 * The complex rollout logic (percent/allowlist) is deprecated.
 */

import { useMemo, useState, useEffect } from "react";
import { trackRenderEvent } from "./telemetry";

/**
 * Get the v2 override from URL query params.
 * Only used for emergency rollback via ?dealCardV2=0
 * 
 * @returns {boolean | null} true = force v2, false = force v1 (emergency), null = use default (v2)
 */
export function getDealCardV2Override(): boolean | null {
  if (typeof window === "undefined") {
    return null;
  }
  
  const qs = new URLSearchParams(window.location.search);
  const override = qs.get("dealCardV2");
  
  if (override === "1") return true;
  if (override === "0") return false;
  return null;
}

/**
 * CR310: Simplified - Always V2 unless emergency override.
 * 
 * @param featureFlag - The dealCardSpecV2_enabled flag value (deprecated, always true now)
 * @param isAdmin - Whether this is an admin context
 * @returns Object with useV2 boolean and debug info
 */
export function shouldUseDealCardV2(
  featureFlag: boolean,
  isAdmin: boolean
): {
  useV2: boolean;
  debug: {
    override: string | null;
    featureFlag: boolean;
    isAdmin: boolean;
    rolloutReason?: string;
  };
} {
  const override = getDealCardV2Override();
  
  // Emergency rollback via query param
  if (override === false) {
    console.warn("[DealCard] Emergency V1 rollback via ?dealCardV2=0");
    return {
      useV2: false,
      debug: {
        override: "0",
        featureFlag,
        isAdmin,
        rolloutReason: "emergency_rollback",
      },
    };
  }
  
  // CR310: V2 is now the default - V1 code is removed
  return {
    useV2: true,
    debug: {
      override: override === true ? "1" : null,
      featureFlag,
      isAdmin,
      rolloutReason: "v2_default",
    },
  };
}

/**
 * React hook for v2 renderer decision - simplified for V2-only world.
 */
export function useDealCardV2Override(
  featureFlag: boolean,
  isAdmin: boolean
) {
  // No async config fetch needed anymore
  const [configLoading] = useState(false);
  
  return useMemo(() => {
    const result = shouldUseDealCardV2(featureFlag, isAdmin);
    return {
      ...result,
      configLoading,
      // Rollout config deprecated but kept for type compatibility
      rolloutConfig: null,
    };
  }, [featureFlag, isAdmin, configLoading]);
}

/**
 * Debug log helper for renderer selection.
 */
export function logDealCardRendererChoice(
  useV2: boolean,
  debug: {
    override: string | null;
    featureFlag: boolean;
    isAdmin: boolean;
    rolloutReason?: string;
  },
  dealTypeKey?: string
): void {
  console.debug("[DealCard] renderer=", useV2 ? "v2" : "v1", {
    dealCardV2Override: debug.override,
    featureFlag: debug.featureFlag,
    isAdmin: debug.isAdmin,
    rolloutReason: debug.rolloutReason,
    dealTypeKey,
  });
  
  // Track telemetry event
  trackRenderEvent(useV2 ? "v2" : "v1", {
    dealType: dealTypeKey,
    rolloutReason: debug.rolloutReason,
  });
}

// ================================================================
// DEPRECATED - Kept for backwards compatibility
// ================================================================

/** @deprecated Rollout config no longer used - V2 is always on */
export const DEFAULT_ROLLOUT_CONFIG = {
  enabled: true,
  mode: "on" as const,
  allowlist: [],
  percent: 100,
};

/** @deprecated Use shouldUseDealCardV2 instead */
export function computeRolloutDecision() {
  return { useV2: true, reason: "v2_default" as const };
}
